import React, { MutableRefObject, ComponentType } from 'react';
import {
  DataGridProps,
  GridApi,
  GridEventListener,
  GridRowEditStartReasons,
  GridRowEditStopReasons,
} from '@mui/x-data-grid';
import { alpha } from '@mui/material/styles';
import { DASHBOARD_BAND_COLOURS } from '@/dashboard/utils/DashboardCalculations/dashboardCalculations.utils';

export const ROW_CUSTOM_STATUS_CLASS = 'ep-data-row-status--';
export const HEADER_LABEL_WITH_SPACE = 'ep-header-label-space';

function EpDataGridHOC(Component: ComponentType<DataGridProps>, ref?: MutableRefObject<GridApi>) {
  const EpDataGrid = ({ ...props }: DataGridProps) => {
    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
      if (
        params.reason === GridRowEditStopReasons.rowFocusOut ||
        params.reason === GridRowEditStopReasons.tabKeyDown ||
        params.reason === GridRowEditStopReasons.escapeKeyDown ||
        params.reason === GridRowEditStopReasons.enterKeyDown ||
        params.reason === GridRowEditStopReasons.shiftTabKeyDown
      ) {
        event.defaultMuiPrevented = true;
      }
    };

    const handleRowEditStart: GridEventListener<'rowEditStart'> = (params, event) => {
      if (
        params.reason === GridRowEditStartReasons.cellDoubleClick ||
        params.reason === GridRowEditStartReasons.deleteKeyDown ||
        params.reason === GridRowEditStartReasons.enterKeyDown ||
        params.reason === GridRowEditStartReasons.printableKeyDown
      ) {
        event.defaultMuiPrevented = true;
      }
    };

    return (
      <Component
        aria-label={props['aria-label']}
        hideFooter
        onRowEditStop={handleRowEditStop}
        onRowEditStart={handleRowEditStart}
        apiRef={ref}
        {...props}
        autosizeOnMount
        autosizeOptions={{
          includeOutliers: true,
          includeHeaders: true,
        }}
        disableColumnMenu
        sx={(theme) => ({
          overflow: 'visible',
          borderLeft: 'none',
          borderTop: 'none',
          borderRight: 'none',
          [`& .${ROW_CUSTOM_STATUS_CLASS}Errors`]: getRowHighlight(
            getBackgroundColor(theme.palette.error.main),
          ),
          [`& .${ROW_CUSTOM_STATUS_CLASS}Warnings`]: getRowHighlight(
            getBackgroundColor(DASHBOARD_BAND_COLOURS.WARNINGS),
          ),
          [`& .${HEADER_LABEL_WITH_SPACE} .MuiBadge-badge.MuiBadge-anchorOriginTopRight`]: {
            right: 4,
          },
        })}
      />
    );
  };

  return EpDataGrid;
}

const getBackgroundColor = (color: string) => alpha(color, 0.08);
const getRowHighlight = (colour: string) => ({
  backgroundColor: colour,
  '&:hover': {
    backgroundColor: colour,
  },
  '&.Mui-selected': {
    backgroundColor: colour,
    '&:hover': {
      backgroundColor: colour,
    },
  },
  '&.Mui-hovered': {
    backgroundColor: colour,
  },
  '&.MuiDataGrid-row': {
    backgroundColor: colour,
    '&.Mui-selected': {
      backgroundColor: colour,
    },
    '&.Mui-hovered': {
      backgroundColor: colour,
    },
  },
});

export default EpDataGridHOC;
